import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2, Truck, ShieldCheck, ShieldAlert } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import AppointmentWarning from "@/components/AppointmentWarning";
import { TIME_RANGES, getMinAppointmentDate, getMaxAppointmentDate, validatePoExpiryAndAppointment, validateOrderPoExpiry, validateReferenceRequiredForAppointment } from "@/utils/appointmentUtils";
import { pickupIssues, dropIssues, issuesMessage } from "@/lib/manifestReady";
import LocationQuickEdit from "@/components/LocationQuickEdit";
import AiDocumentExtract from "@/components/AiDocumentExtract";
import { applyExtraction as mergeExtraction, revertExtraction, emptyOwnership } from "@/utils/aiExtractionState";

const DRAFT_KEY = "admin-create-order:draft";

// Carrier insurance is not offered right now — every B2B shipment ships owner-risk.
// Flip to true to bring back the "Get Carrier Insurance" option.
const OFFER_CARRIER_INSURANCE = false;

const CreateOrder = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [dropLocations, setDropLocations] = useState([]);
  const [marketplaces, setMarketplaces] = useState([]);
  const [shippingPartners, setShippingPartners] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [insuranceOption, setInsuranceOption] = useState("owner_risk"); // owner-risk default; carrier-insurance option hidden (OFFER_CARRIER_INSURANCE)
  const [showOwnerRiskDialog, setShowOwnerRiskDialog] = useState(false);
  const [showAddDropLocationDialog, setShowAddDropLocationDialog] = useState(false);
  const [saveDropLocationForFuture, setSaveDropLocationForFuture] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  
  // Selected seller wallet info
  const [selectedSellerWallet, setSelectedSellerWallet] = useState(null);
  const [isSellerLowBalance, setIsSellerLowBalance] = useState(false);
  // Billing (commercial) structure gate — no structure => order can't be billed => block.
  const [isBillingNotSet, setIsBillingNotSet] = useState(false);
  
  const [tempDropLocation, setTempDropLocation] = useState({
    company_name: "",
    contact_person_name: "",
    drop_contact: "",
    email: "",
    address_line: "",
    country: "India",
    pincode: "",
    city: "",
    state: ""
  });
  const [selectedPickupLocation, setSelectedPickupLocation] = useState(null);
  const [selectedDropLocation, setSelectedDropLocation] = useState(null);

  const [formData, setFormData] = useState({
    seller_id: "",
    lrn: "",
    shipping_partner_id: "",
    actual_shipping_partner: "",
    pickup_location_id: "",
    drop_location_id: "",
    pickup_type: "fm_pickup",
    description: "",
    b2b_marketplace_id: "",
    po_expiry_date: "",
    appointment_date: "",
    appointment_time_range: "",
    appointment_id: "",
    number_of_boxes: 1,
    payment_mode: "Prepaid",
    freight_mode: "BTC",
    eway_bill_number: "",
    skip_eway_bill: false,
    invoice_document: null,
    secondary_document: null
  });

  // Multiple reference numbers with dates
  const [referenceNumbers, setReferenceNumbers] = useState([
    { reference_number: "", date: "" }
  ]);

  // Multiple invoices
  const [invoices, setInvoices] = useState([
    { invoice_number: "", invoice_amount: "" }
  ]);

  const [boxes, setBoxes] = useState([
    { length: "", width: "", height: "", weight: "", quantity: 1 }
  ]);

  // --- AI auto-fill (B2B only) -------------------------------------------
  // The document uploaded here is an EXTRACTION document. It is never treated
  // as the order's invoice unless the user explicitly clicks "Attach as
  // Invoice", which is the only place aiAttachedAsInvoice is set true.
  const [aiDocument, setAiDocument] = useState(null);
  const [aiAttachedAsInvoice, setAiAttachedAsInvoice] = useState(false);

  useEffect(() => {
    fetchSellers();
    fetchMarketplaces();
    fetchShippingPartners();
  }, []);

  useEffect(() => {
    if (formData.seller_id) {
      fetchLocationsForSeller(formData.seller_id);
      // Check seller wallet balance
      checkSellerWalletBalance(formData.seller_id);
    } else {
      setSelectedSellerWallet(null);
      setIsSellerLowBalance(false);
      setIsBillingNotSet(false);
    }
  }, [formData.seller_id]);

  // Re-run the wallet/billing check once the sellers list finishes loading — a draft-restored
  // seller_id fires the effect above before `sellers` is populated, which would leave the
  // wallet/billing gate stale (find() returns undefined => flags default to false).
  useEffect(() => {
    if (formData.seller_id && sellers.length) checkSellerWalletBalance(formData.seller_id);
    // eslint-disable-next-line
  }, [sellers]);

  // --- Draft persistence: keep the in-progress order across reloads / navigation.
  // File inputs (invoice/secondary document) can't be serialized, so they re-attach.
  const firstPersist = useRef(true);
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (saved) {
        if (saved.formData) setFormData((f) => ({ ...f, ...saved.formData, invoice_document: null, secondary_document: null }));
        if (Array.isArray(saved.referenceNumbers)) setReferenceNumbers(saved.referenceNumbers);
        if (Array.isArray(saved.invoices)) setInvoices(saved.invoices);
        if (Array.isArray(saved.boxes)) setBoxes(saved.boxes);
        // Without this the restored values look user-typed, and the next
        // document can neither replace nor revert them. See aiOwnedRef.
        if (saved.aiOwned) aiOwnedRef.current = saved.aiOwned;
        if (saved.insuranceOption) setInsuranceOption(saved.insuranceOption);
      }
    } catch { /* ignore a corrupt draft */ }
  }, []);
  useEffect(() => {
    if (firstPersist.current) { firstPersist.current = false; return; }  // don't overwrite before restore
    try {
      const { invoice_document, secondary_document, ...rest } = formData;
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ formData: rest, referenceNumbers, invoices, boxes, insuranceOption, aiOwned: aiOwnedRef.current }));
    } catch { /* quota / serialize errors are non-fatal */ }
  }, [formData, referenceNumbers, invoices, boxes, insuranceOption]);

  const checkSellerWalletBalance = async (sellerId) => {
    try {
      const seller = sellers.find(s => s.seller_id === sellerId);
      if (seller) {
        const balance = seller.wallet_balance || 0;
        const minBalance = seller.min_wallet_balance || 0;
        const enforceWalletMinimum = seller.enforce_wallet_minimum_for_orders !== false;
        setSelectedSellerWallet({ balance, minBalance, enforceWalletMinimum });
        // Only flag as low balance if enforcement is enabled
        setIsSellerLowBalance(enforceWalletMinimum && balance <= minBalance);
        // Billing structure gate — the sellers list already carries commercial_structures.
        setIsBillingNotSet(!(seller.commercial_structures?.length));
      }
    } catch (error) {
      console.error("Failed to check seller wallet:", error);
    }
  };

  useEffect(() => {
    const selected = pickupLocations.find(loc => loc.id === formData.pickup_location_id);
    setSelectedPickupLocation(selected || null);
  }, [formData.pickup_location_id, pickupLocations]);

  useEffect(() => {
    const selected = dropLocations.find(loc => loc.id === formData.drop_location_id);
    setSelectedDropLocation(selected || null);
  }, [formData.drop_location_id, dropLocations]);

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers-org/`);
      setSellers(response.data);
    } catch (error) {
      toast.error("Failed to fetch sellers");
    }
  };

  const fetchMarketplaces = async () => {
    try {
      const response = await axios.get(`${API}/b2b-marketplace/active`);
      setMarketplaces(response.data);
    } catch (error) {
      console.error("Failed to fetch marketplaces");
    }
  };

  const fetchShippingPartners = async () => {
    try {
      const response = await axios.get(`${API}/b2b-shipping-partner/`);
      setShippingPartners(response.data.filter(sp => sp.is_active));
    } catch (error) {
      console.error("Failed to fetch shipping partners");
    }
  };

  const fetchLocationsForSeller = async (sellerId) => {
    try {
      const [pickupRes, dropRes] = await Promise.all([
        axios.get(`${API}/pickup-locations/seller/${sellerId}`),
        axios.get(`${API}/drop-locations/seller/${sellerId}`)
      ]);
      setPickupLocations(pickupRes.data);
      setDropLocations(dropRes.data);
    } catch (error) {
      toast.error("Failed to fetch locations for selected seller");
    }
  };

  const handleAddBox = () => {
    setBoxes([...boxes, { length: "", width: "", height: "", weight: "", quantity: 1 }]);
  };

  const handleRemoveBox = (index) => {
    if (boxes.length > 1) {
      setBoxes(boxes.filter((_, i) => i !== index));
    }
  };

  const handleBoxChange = (index, field, value) => {
    const newBoxes = [...boxes];
    newBoxes[index][field] = value;
    setBoxes(newBoxes);
  };

  const handleFileChange = (field, file) => {
    if (file && file.size > 20 * 1024 * 1024) {
      toast.error("File size must be less than 20MB");
      return;
    }
    setFormData({ ...formData, [field]: file });
  };

  // --- AI auto-fill handlers ---------------------------------------------

  // Snapshot of what the current extraction wrote, and what preceded it. A field
  // stays "AI-owned" only while its value still matches what the AI wrote, so a
  // later document may replace it — but anything the user edited is theirs and
  // is never overwritten or reverted. See utils/aiExtractionState.
  const aiOwnedRef = useRef(emptyOwnership());

  const currentFormState = () => ({ formData, referenceNumbers, invoices, boxes });

  const commitFormState = (next) => {
    setFormData(next.formData);
    setReferenceNumbers(next.referenceNumbers);
    setInvoices(next.invoices);
    setBoxes(next.boxes);
    aiOwnedRef.current = next.owned;
  };

  // Replacing or clearing the document first undoes whatever the previous
  // extraction still owns, so no field of the old document can survive beside
  // the new one's values.
  const handleAiDocumentChange = (file) => {
    const undone = revertExtraction(currentFormState(), aiOwnedRef.current);
    commitFormState(undone);
    setAiDocument(file);

    if (aiAttachedAsInvoice) {
      setAiAttachedAsInvoice(false);
      setFormData((prev) => ({ ...prev, invoice_document: null }));
      toast.info("Invoice attachment removed \u2014 the document changed.");
    }
    if (undone.reverted.length) {
      toast.info(`Cleared ${undone.reverted.length} auto-filled field(s) from the previous document.`);
    }
  };

  // The ONLY place the extraction document becomes the invoice. Reuses the
  // original File, so nothing is uploaded or converted twice.
  const handleAttachAsInvoice = (file) => {
    setFormData((prev) => ({ ...prev, invoice_document: file }));
    setAiAttachedAsInvoice(true);
    toast.success(`"${file.name}" attached as the Invoice Document`);
  };

  const handleDetachInvoice = () => {
    setAiAttachedAsInvoice(false);
    setFormData((prev) => ({ ...prev, invoice_document: null }));
  };

  const applyExtraction = (data, debug) => {
    if (!data) return;
    const result = mergeExtraction(currentFormState(), data, aiOwnedRef.current, {
      dropLocations,
    });
    commitFormState(result);

    // The four stages data can be lost between, in one place. Only printed when
    // the backend is running with DOCUMENT_EXTRACTION_DEBUG enabled.
    if (debug) {
      // eslint-disable-next-line no-console
      console.debug("[ai-extract] pipeline", {
        gemini_extraction: debug.gemini_raw,
        normalized_extraction: debug.normalized,
        frontend_received: data,
        formData_after_merge: result.formData,
        rows_after_merge: {
          referenceNumbers: result.referenceNumbers,
          invoices: result.invoices,
          boxes: result.boxes,
        },
        outcome: {
          filled: result.filled,
          replaced: result.replaced,
          skipped: result.skipped,
          matchedDropLocation: result.matchedDropLocation,
        },
        backend: {
          // Matches `trace=` in the backend log line for the same request.
          trace_id: debug.trace_id,
          elapsed_ms: debug.elapsed_ms,
          field_provenance: debug.field_provenance,
          model: debug.model,
          sent_as: debug.sent_as,
          content_type: debug.content_type,
          bytes: debug.bytes,
          model_returned_nothing: debug.model_returned_nothing,
          dropped_by_normalization: debug.dropped_by_normalization,
          keys_model_invented: debug.keys_model_invented,
        },
      });
    }

    const { filled, replaced, skipped } = result;
    setTimeout(() => {
      const parts = [];
      if (filled.length) parts.push(`filled ${filled.length}`);
      if (replaced.length) parts.push(`replaced ${replaced.length}`);
      if (!parts.length) {
        // Nothing moved. Saying so plainly beats a green tick that looks
        // like it worked — and names what is standing in the way.
        toast.info(
          skipped.length
            ? `Nothing auto-filled — these already have values: ${skipped.join(", ")}. Clear a field to take the document's value.`
            : "Couldn't find matching order details in this document. You can enter the information manually."
        );
        return;
      }
      const head = `Auto-fill: ${parts.join(", ")} field(s).`;
      // Name the drop location explicitly — it is the one auto-filled value
      // whose mistake would misdeliver the freight.
      if (result.matchedDropLocation) {
        toast.info(`Drop location set to "${result.matchedDropLocation.company_name}" — please confirm it.`);
      }
      toast.success(
        skipped.length
          ? `${head} Kept your own values for: ${skipped.join(", ")}.`
          : `${head} Please review before creating the order.`
      );
    }, 0);
  };

  // Reference Numbers handlers
  const handleReferenceNumberChange = (index, field, value) => {
    const updated = [...referenceNumbers];
    updated[index][field] = value;
    setReferenceNumbers(updated);
  };

  const addReferenceNumber = () => {
    setReferenceNumbers([...referenceNumbers, { reference_number: "", date: "" }]);
  };

  const removeReferenceNumber = (index) => {
    if (referenceNumbers.length > 1) {
      setReferenceNumbers(referenceNumbers.filter((_, i) => i !== index));
    }
  };

  // Invoices handlers
  const handleInvoiceChange = (index, field, value) => {
    const updated = [...invoices];
    // Invoice number: keep only the courier-allowed charset (letters, digits, / and -)
    // as the user types, so a stray space/punctuation can't 400 the manifest later.
    updated[index][field] = field === 'invoice_number'
      ? value.replace(/[^A-Za-z0-9/-]/g, '')
      : value;
    setInvoices(updated);
  };

  const addInvoice = () => {
    setInvoices([...invoices, { invoice_number: "", invoice_amount: "" }]);
  };

  const removeInvoice = (index) => {
    if (invoices.length > 1) {
      setInvoices(invoices.filter((_, i) => i !== index));
    }
  };

  // Calculate total invoice amount
  const totalInvoiceAmount = invoices.reduce((sum, inv) => {
    const amount = parseFloat(inv.invoice_amount) || 0;
    return sum + amount;
  }, 0);

  const lookupPincode = async (pincode) => {
    if (pincode.length !== 6) return;
    setPincodeLoading(true);
    try {
      const response = await axios.get(`${API}/pickup-locations/pincode/${pincode}`);
      setTempDropLocation(prev => ({
        ...prev,
        city: response.data.city,
        state: response.data.state
      }));
    } catch (error) {
      toast.error("Invalid pincode or unable to fetch location data");
    } finally {
      setPincodeLoading(false);
    }
  };

  const handleAddTempDropLocation = async () => {
    // Validate 10-digit mobile number
    if (!tempDropLocation.drop_contact || tempDropLocation.drop_contact.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    
    try {
      if (saveDropLocationForFuture) {
        // Save to database
        const payload = {
          ...tempDropLocation,
          seller_id: formData.seller_id
        };
        const response = await axios.post(`${API}/drop-locations/`, payload);
        setDropLocations([...dropLocations, response.data]);
        setFormData({ ...formData, drop_location_id: response.data.id });
        setSelectedDropLocation(response.data);
        toast.success("Drop location saved successfully!");
      } else {
        // Use temporarily for this order only
        const tempLoc = { ...tempDropLocation, id: 'temp_' + Date.now() };
        setFormData({ ...formData, drop_location_id: tempLoc.id });
        setSelectedDropLocation(tempLoc);
        toast.success("Drop location added temporarily for this order");
      }
      setShowAddDropLocationDialog(false);
      setTempDropLocation({
        company_name: "",
        contact_person_name: "",
        drop_contact: "",
        email: "",
        address_line: "",
        country: "India",
        pincode: "",
        city: "",
        state: ""
      });
      setSaveDropLocationForFuture(false);
    } catch (error) {
      toast.error("Failed to add drop location");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.seller_id) {
        toast.error("Please select a seller");
        setSubmitting(false);
        return;
      }

      if (isBillingNotSet) {
        toast.error("Billing Structure not Set. Configure the seller's billing structure before creating orders.");
        setSubmitting(false);
        return;
      }

      // Insurance selection is mandatory (no default)
      if (!insuranceOption) {
        toast.error("Please select an insurance option for the shipment");
        setSubmitting(false);
        return;
      }

      // Validate boxes
      const validBoxes = boxes.filter(box => 
        box.length && box.width && box.height && box.weight && box.quantity
      );

      if (validBoxes.length === 0) {
        toast.error("Please provide at least one complete box dimension");
        setSubmitting(false);
        return;
      }

      if (!formData.invoice_document) {
        toast.error("Invoice document is required");
        setSubmitting(false);
        return;
      }

      // Validate invoices
      const validInvoices = invoices.filter(inv => inv.invoice_number && inv.invoice_amount);
      if (validInvoices.length === 0) {
        toast.error("Please provide at least one invoice with number and amount");
        setSubmitting(false);
        return;
      }

      // Invoice number must match the courier's charset (letters, digits, / and - only).
      const badInv = validInvoices.find(inv => !/^[A-Za-z0-9/-]+$/.test(String(inv.invoice_number).trim()));
      if (badInv) {
        toast.error(`Invoice number "${badInv.invoice_number}" is invalid — use only letters, digits, / and - (no spaces or special characters).`);
        setSubmitting(false);
        return;
      }

      // Goods description is mandatory (the courier rejects an empty shipment description).
      if (!formData.description || !formData.description.trim()) {
        toast.error("Please enter a goods description for the shipment");
        setSubmitting(false);
        return;
      }

      // Validate LRN and shipping partner
      if (formData.lrn && !formData.shipping_partner_id) {
        toast.error("Shipping partner is required when LR number is provided");
        setSubmitting(false);
        return;
      }

      if (formData.lrn && formData.shipping_partner_id) {
        const selectedPartner = shippingPartners.find(p => p.id === formData.shipping_partner_id);
        if (selectedPartner?.is_aggregator && !formData.actual_shipping_partner) {
          toast.error("Actual shipping partner is required for aggregators");
          setSubmitting(false);
          return;
        }
      }

      // If an appointment date is chosen, a time range is mandatory — the backend
      // only persists the appointment when BOTH are present, so without this the
      // appointment would be silently dropped and the order wouldn't appear in the
      // Appointments section.
      if (formData.appointment_date && !formData.appointment_time_range) {
        toast.error("Please select a Time Range for the appointment date");
        setSubmitting(false);
        return;
      }

      // A Delhivery Appointment ID is required only when the selected B2B marketplace opts in.
      const apptIdRequired = !!marketplaces.find((m) => m.id === formData.b2b_marketplace_id)?.appointment_id_required;
      if (formData.appointment_date && apptIdRequired && !(formData.appointment_id || "").trim()) {
        toast.error("Appointment ID is required to book an appointment for this marketplace.");
        setSubmitting(false);
        return;
      }

      // If an appointment is set, a reference/PO number is required (appointment => reference
      // => date + PO expiry). A reference alone does NOT require an appointment.
      const refReqErr = validateReferenceRequiredForAppointment(referenceNumbers, formData.appointment_date);
      if (refReqErr) {
        toast.error(refReqErr);
        setSubmitting(false);
        return;
      }

      // PO expiry rules: a reference number needs a PO expiry date, and the
      // appointment must not be later than the earliest PO expiry.
      const poErr = validatePoExpiryAndAppointment(referenceNumbers, formData.appointment_date);
      if (poErr) {
        toast.error(poErr);
        setSubmitting(false);
        return;
      }

      // Order-level PO Expiry Date is mandatory when a reference/PO number is entered.
      const poExpiryErr = validateOrderPoExpiry(referenceNumbers, formData.po_expiry_date, formData.appointment_date);
      if (poExpiryErr) {
        toast.error(poExpiryErr);
        setSubmitting(false);
        return;
      }

      // Filter valid reference numbers (non-empty reference_number)
      const validReferenceNumbers = referenceNumbers.filter(ref => ref.reference_number);

      const formDataToSend = new FormData();
      formDataToSend.append('seller_id', formData.seller_id);
      formDataToSend.append('pickup_location_id', formData.pickup_location_id);
      formDataToSend.append('drop_location_id', formData.drop_location_id);
      formDataToSend.append('number_of_boxes', validBoxes.length);
      formDataToSend.append('boxes', JSON.stringify(validBoxes.map(box => ({
        length: parseFloat(box.length),
        width: parseFloat(box.width),
        height: parseFloat(box.height),
        weight: parseFloat(box.weight),
        quantity: parseInt(box.quantity)
      }))));
      
      // Send invoices array
      formDataToSend.append('invoices', JSON.stringify(validInvoices.map(inv => ({
        invoice_number: inv.invoice_number,
        invoice_amount: parseFloat(inv.invoice_amount)
      }))));
      
      // Send reference numbers array if any
      if (validReferenceNumbers.length > 0) {
        formDataToSend.append('reference_numbers', JSON.stringify(validReferenceNumbers));
      }

      // Order-level PO Expiry Date
      if (formData.po_expiry_date) {
        formDataToSend.append('po_expiry_date', formData.po_expiry_date);
      }

      formDataToSend.append('invoice_document', formData.invoice_document);
      
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.b2b_marketplace_id) formDataToSend.append('b2b_marketplace_id', formData.b2b_marketplace_id);
      if (formData.appointment_date) formDataToSend.append('appointment_date', formData.appointment_date);
      if (formData.appointment_time_range) formDataToSend.append('appointment_time_range', formData.appointment_time_range);
      if (formData.appointment_date) formDataToSend.append('appointment_id', (formData.appointment_id || "").trim());
      formDataToSend.append('insurance_option', insuranceOption);
      formDataToSend.append('payment_mode', formData.payment_mode);
      formDataToSend.append('skip_eway_bill', formData.skip_eway_bill);
      if (formData.eway_bill_number && !formData.skip_eway_bill) {
        formDataToSend.append('eway_bill_number', formData.eway_bill_number);
      }
      formDataToSend.append('freight_mode', formData.freight_mode);
      formDataToSend.append('pickup_type', 'fm_pickup'); // Fixed to FM pickup for now; future: allow seller pickup
      if (formData.lrn) formDataToSend.append('lrn', formData.lrn);
      if (formData.shipping_partner_id) formDataToSend.append('shipping_partner_id', formData.shipping_partner_id);
      if (formData.actual_shipping_partner) formDataToSend.append('actual_shipping_partner', formData.actual_shipping_partner);
      
      if (formData.secondary_document) {
        formDataToSend.append('secondary_document', formData.secondary_document);
      }

      await axios.post(`${API}/orders/`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Order created successfully!");
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      navigate('/admin/orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin/orders')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-slate-800">Create Order</h1>
            <p className="text-slate-600">Create a new delivery order for a seller</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Seller Selection</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Seller *</Label>
                <SearchableSelect
                  value={formData.seller_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, seller_id: value, pickup_location_id: "", drop_location_id: "" });
                    setPickupLocations([]);
                    setDropLocations([]);
                  }}
                  placeholder="Select seller organization"
                  emptyText="No sellers found."
                  options={sellers.map((seller) => ({
                    value: seller.seller_id,
                    label: `${seller.brand_name || seller.company_registered_name}_${seller.seller_id}`,
                    searchLabel: `${seller.brand_name} ${seller.company_registered_name} ${seller.seller_id}`,
                  }))}
                />
              </div>

              {/* Low Balance Alert */}
              {isSellerLowBalance && selectedSellerWallet && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-red-600 font-semibold">⚠️ Low Balance Alert</span>
                  </div>
                  <p className="text-sm text-red-700 mt-1">
                    This seller&apos;s wallet balance (₹{selectedSellerWallet.balance?.toLocaleString()}) is below the minimum threshold 
                    (₹{selectedSellerWallet.minBalance?.toLocaleString()}). Order creation is not allowed.
                  </p>
                </div>
              )}

              {/* Billing Structure Not Set Alert */}
              {isBillingNotSet && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-red-600 font-semibold">⚠️ Billing Structure not Set</span>
                  </div>
                  <p className="text-sm text-red-700 mt-1">
                    This seller has no billing (commercial) structure configured, so orders can&apos;t be billed.
                    Order creation is not allowed until the billing structure is set.
                  </p>
                </div>
              )}

              {/* LR Number and Shipping Partner - hidden when low balance / billing not set */}
              {!isSellerLowBalance && !isBillingNotSet && (
              <>
              <div className="space-y-2 mt-4">
                <Label>LR Number (Optional)</Label>
                <Input
                  value={formData.lrn}
                  onChange={(e) => setFormData({ ...formData, lrn: e.target.value })}
                  placeholder="Enter Logistics Request Number"
                />
                <p className="text-xs text-slate-500">If you have an LR number, shipping partner selection becomes mandatory</p>
              </div>

              {formData.lrn && (
                <>
                  <div className="space-y-2 mt-4">
                    <Label>Shipping Partner *</Label>
                    <Select
                      value={formData.shipping_partner_id}
                      onValueChange={(value) => setFormData({ ...formData, shipping_partner_id: value, actual_shipping_partner: "" })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select shipping partner" />
                      </SelectTrigger>
                      <SelectContent>
                        {shippingPartners.map((partner) => (
                          <SelectItem key={partner.id} value={partner.id}>
                            {partner.name} {partner.is_aggregator && "(Aggregator)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.shipping_partner_id && shippingPartners.find(p => p.id === formData.shipping_partner_id)?.is_aggregator && (
                    <div className="space-y-2 mt-4">
                      <Label>Actual Shipping Partner *</Label>
                      <Input
                        value={formData.actual_shipping_partner}
                        onChange={(e) => setFormData({ ...formData, actual_shipping_partner: e.target.value })}
                        placeholder="Enter actual shipping partner name (e.g., Delhivery, Blue Dart)"
                        required
                      />
                      <p className="text-xs text-slate-500">This aggregator uses multiple carriers. Specify the actual carrier</p>
                    </div>
                  )}
                </>
              )}
              </>
              )}
            </CardContent>
          </Card>

          {formData.seller_id && !isSellerLowBalance && !isBillingNotSet && (
            <>
              <Card className="border-0 shadow-lg mt-6">
                <CardHeader>
                  <CardTitle>Pickup Type *</CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <button
                      type="button"
                      data-testid="pickup-type-fm"
                      className={`flex items-center gap-3 px-5 py-3 rounded-lg border-2 transition-all ${formData.pickup_type === "fm_pickup" ? "border-[#00A699] bg-[#00A699]/5 text-[#00A699]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                      onClick={() => setFormData({ ...formData, pickup_type: "fm_pickup" })}
                    >
                      <Truck className="w-5 h-5" />
                      <div className="text-left">
                        <div className="font-semibold text-sm">FM Pickup</div>
                        <div className="text-xs opacity-70">Delhivery picks up from your location</div>
                      </div>
                    </button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg mt-6">
                <CardHeader>
                  <CardTitle>Location Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pickup Location *</Label>
                      <SearchableSelect
                        value={formData.pickup_location_id}
                        onValueChange={(value) => setFormData({ ...formData, pickup_location_id: value })}
                        placeholder="Select pickup location"
                        emptyText="No pickup locations found."
                        options={pickupLocations.map((loc) => ({
                          value: loc.id,
                          label: `${loc.facility_name} - ${loc.city}`,
                          searchLabel: `${loc.facility_name} ${loc.city} ${loc.pincode} ${loc.address_line}`,
                        }))}
                      />
                      {selectedPickupLocation && (
                        <div className="mt-2 p-4 bg-slate-50 rounded-md text-sm">
                          <p className="font-medium text-slate-900">{selectedPickupLocation.facility_name}</p>
                          <p className="text-slate-600">{selectedPickupLocation.address_line}</p>
                          <p className="text-slate-600">{selectedPickupLocation.city}, {selectedPickupLocation.state} - {selectedPickupLocation.pincode}</p>
                          <p className="text-slate-500">Contact: {selectedPickupLocation.contact_phone || selectedPickupLocation.pickup_contact || "—"}</p>
                        </div>
                      )}
                      {selectedPickupLocation && (() => {
                        const issues = pickupIssues(selectedPickupLocation);
                        return (
                          <>
                            {issues.length ? <p className="text-xs text-red-600 mt-1">{issuesMessage("pickup", selectedPickupLocation?.facility_name, issues)}</p> : null}
                            <LocationQuickEdit kind="pickup" location={selectedPickupLocation} onSaved={() => fetchLocationsForSeller(formData.seller_id)} />
                          </>
                        );
                      })()}
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label>Drop Location *</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setShowAddDropLocationDialog(true)}
                          disabled={!formData.seller_id}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Drop Location
                        </Button>
                      </div>
                      <SearchableSelect
                        value={formData.drop_location_id}
                        onValueChange={(value) => setFormData({ ...formData, drop_location_id: value })}
                        placeholder="Select drop location"
                        emptyText="No drop locations found."
                        options={dropLocations.map((loc) => ({
                          value: loc.id,
                          label: `${loc.company_name} - ${loc.city}`,
                          searchLabel: `${loc.company_name} ${loc.city} ${loc.pincode} ${loc.address_line} ${loc.contact_person_name || ""}`,
                        }))}
                      />
                      {selectedDropLocation && (
                        <div className="mt-2 p-4 bg-slate-50 rounded-md text-sm">
                          <p className="font-medium text-slate-900">{selectedDropLocation.company_name}</p>
                          <p className="text-slate-600">{selectedDropLocation.address_line}</p>
                          <p className="text-slate-600">{selectedDropLocation.city}, {selectedDropLocation.state} - {selectedDropLocation.pincode}</p>
                          <p className="text-slate-500">Contact: {selectedDropLocation.drop_contact}</p>
                        </div>
                      )}
                      {selectedDropLocation && (() => {
                        const issues = dropIssues(selectedDropLocation);
                        return (
                          <>
                            {issues.length ? <p className="text-xs text-red-600 mt-1">{issuesMessage("drop", selectedDropLocation?.company_name || selectedDropLocation?.contact_person_name, issues)}</p> : null}
                            {!selectedDropLocation.id?.startsWith('temp_') && <LocationQuickEdit kind="drop" location={selectedDropLocation} onSaved={() => fetchLocationsForSeller(formData.seller_id)} />}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Shipment Insurance — mandatory selection, no default */}
              <Card className="border-0 shadow-lg mt-6">
                <CardHeader>
                  <CardTitle>Insure your shipment *</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">This shipment will be shipped at the owner&apos;s risk.</p>
                  <div className={`grid grid-cols-1 gap-4 ${OFFER_CARRIER_INSURANCE ? "sm:grid-cols-2" : ""}`}>
                    <button
                      type="button"
                      data-testid="insurance-owner-risk"
                      onClick={() => { setInsuranceOption("owner_risk"); setShowOwnerRiskDialog(true); }}
                      className={`flex items-start gap-3 px-5 py-4 rounded-lg border-2 transition-all text-left ${insuranceOption === "owner_risk" ? "border-amber-400 bg-amber-50 text-amber-700 ring-2 ring-amber-200 shadow-sm" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                    >
                      <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-sm">Ship with Owner&apos;s Risk</div>
                        <div className="text-xs opacity-70">{"On selecting this option, the shipment will be shipped on owner's risk, with no insurance applicable from Carrier's end."}</div>
                      </div>
                    </button>
                    {OFFER_CARRIER_INSURANCE && (
                    <button
                      type="button"
                      data-testid="insurance-carrier"
                      onClick={() => setInsuranceOption("carrier_insurance")}
                      className={`flex items-start gap-3 px-5 py-4 rounded-lg border-2 transition-all text-left ${insuranceOption === "carrier_insurance" ? "border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200 shadow-sm" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                    >
                      <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-sm">Get Carrier Insurance</div>
                        <div className="text-xs opacity-70">{"Insurance will be applicable as per your agreement with the insurance provider."}</div>
                      </div>
                    </button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg mt-6">
                <CardHeader>
                  <CardTitle>Order Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <AiDocumentExtract
                    document={aiDocument}
                    onDocumentChange={handleAiDocumentChange}
                    onExtracted={applyExtraction}
                    onAttachAsInvoice={handleAttachAsInvoice}
                    attachedAsInvoice={aiAttachedAsInvoice}
                    onDetachInvoice={handleDetachInvoice}
                    disabled={submitting}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="description">Description <span className="text-red-500">*</span></Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      onBlur={(e) => setFormData({ ...formData, description: e.target.value.trim() })}
                      placeholder="Goods description (e.g. Apparel, Electronics)"
                      required
                    />
                    <p className="text-xs text-slate-500">Required by the courier — describe the goods being shipped.</p>
                  </div>

                  {/* Multiple Reference Numbers */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label>Reference/PO Numbers</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addReferenceNumber}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Reference
                      </Button>
                    </div>
                    {formData.appointment_date && !referenceNumbers.some((r) => (r.reference_number || "").trim()) && (
                      <p className="text-xs text-red-500">
                        A reference/PO number is required when an appointment date is set (with its date and PO expiry).
                      </p>
                    )}
                    {referenceNumbers.map((ref, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-slate-500">
                            Reference Number{formData.appointment_date ? " *" : ""}
                          </Label>
                          <Input
                            value={ref.reference_number}
                            onChange={(e) => handleReferenceNumberChange(index, 'reference_number', e.target.value)}
                            onBlur={(e) => handleReferenceNumberChange(index, 'reference_number', e.target.value.trim())}
                            placeholder="PO/Reference number"
                          />
                        </div>
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-slate-500">Date{ref.reference_number ? " *" : ""}</Label>
                          <Input
                            type="date"
                            value={ref.date}
                            onChange={(e) => handleReferenceNumberChange(index, 'date', e.target.value)}
                          />
                        </div>
                        {referenceNumbers.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeReferenceNumber(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}

                    {/* Order-level PO Expiry Date — mandatory when any reference/PO number is entered */}
                    <div className="space-y-1 max-w-xs">
                      <Label className="text-xs text-slate-500">
                        PO Expiry Date{referenceNumbers.some((r) => (r.reference_number || "").trim()) ? " *" : ""}
                      </Label>
                      <Input
                        type="date"
                        value={formData.po_expiry_date}
                        onChange={(e) => setFormData({ ...formData, po_expiry_date: e.target.value })}
                      />
                      <p className="text-[11px] text-slate-400">Required when a reference/PO number is entered.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="b2b_marketplace">B2B Marketplace (Optional)</Label>
                      <SearchableSelect
                        value={formData.b2b_marketplace_id}
                        onValueChange={(value) => setFormData({ ...formData, b2b_marketplace_id: value })}
                        placeholder="Select marketplace (Optional)"
                        emptyText="No marketplaces found."
                        options={[
                          ...marketplaces.map((m) => ({
                            value: m.id,
                            label: m.name,
                            render: (
                              <div className="flex items-center gap-2">
                                {m.logo_url && <img src={m.logo_url} alt={m.name} className="h-4 w-4 object-contain" />}
                                <span>{m.name}</span>
                              </div>
                            ),
                          })),
                          { value: "Others", label: "Others" },
                        ]}
                      />
                    </div>
                    <div className="space-y-4">
                      <AppointmentWarning />
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="appointment_date">Appointment Date (Optional)</Label>
                          <Input
                            id="appointment_date"
                            type="date"
                            value={formData.appointment_date}
                            onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                            min={getMinAppointmentDate().toISOString().split('T')[0]}
                            max={getMaxAppointmentDate().toISOString().split('T')[0]}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="appointment_time_range">Time Range</Label>
                          <Select
                            value={formData.appointment_time_range}
                            onValueChange={(value) => setFormData({ ...formData, appointment_time_range: value })}
                            disabled={!formData.appointment_date}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select time range" />
                            </SelectTrigger>
                            <SelectContent>
                              {TIME_RANGES.map((range) => (
                                <SelectItem key={range.value} value={range.value}>
                                  {range.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {formData.appointment_date && (() => {
                        const apptIdRequired = !!marketplaces.find((m) => m.id === formData.b2b_marketplace_id)?.appointment_id_required;
                        return (
                        <div className="space-y-2 max-w-md">
                          <Label htmlFor="appointment_id">Delhivery Appointment ID{apptIdRequired ? " *" : " (optional)"}</Label>
                          <Input
                            id="appointment_id"
                            value={formData.appointment_id}
                            onChange={(e) => setFormData({ ...formData, appointment_id: e.target.value })}
                            placeholder="Enter Delhivery appointment ID"
                          />
                          {apptIdRequired && <p className="text-xs text-red-500">Required to book the appointment for this marketplace.</p>}
                        </div>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg mt-6">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Box Dimensions</CardTitle>
                    <Button type="button" size="sm" onClick={handleAddBox} style={{backgroundColor: '#00A699'}}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Box
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {boxes.map((box, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-slate-700">Box {index + 1}</span>
                        {boxes.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => handleRemoveBox(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-5 gap-4">
                        <div className="space-y-2">
                          <Label>Length (cm) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={box.length}
                            onChange={(e) => handleBoxChange(index, 'length', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Width (cm) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={box.width}
                            onChange={(e) => handleBoxChange(index, 'width', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Height (cm) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={box.height}
                            onChange={(e) => handleBoxChange(index, 'height', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Weight (kg) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={box.weight}
                            onChange={(e) => handleBoxChange(index, 'weight', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={box.quantity}
                            onChange={(e) => handleBoxChange(index, 'quantity', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg mt-6">
                <CardHeader>
                  <CardTitle>Invoice Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Mode *</Label>
                      <Select
                        value={formData.payment_mode}
                        onValueChange={(value) => setFormData({ ...formData, payment_mode: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Prepaid">Prepaid</SelectItem>
                          <SelectItem value="COD">COD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Freight Mode</Label>
                      <p className="text-sm text-slate-500">BTC - Bill to Customer</p>
                    </div>
                  </div>

                  {/* Multiple Invoices */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label>Invoices *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addInvoice}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Invoice
                      </Button>
                    </div>
                    {invoices.map((inv, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-slate-500">Invoice Number *</Label>
                          <Input
                            value={inv.invoice_number}
                            onChange={(e) => handleInvoiceChange(index, 'invoice_number', e.target.value)}
                            placeholder="e.g. INV-001 or DEL/1122/0095"
                            required
                          />
                          <p className="text-[11px] text-slate-400">Letters, digits, / and - only.</p>
                        </div>
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-slate-500">Invoice Amount (₹) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={inv.invoice_amount}
                            onChange={(e) => handleInvoiceChange(index, 'invoice_amount', e.target.value)}
                            placeholder="Amount"
                            required
                          />
                        </div>
                        {invoices.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeInvoice(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {/* Total Invoice Amount */}
                    <div className="flex justify-end pt-2 border-t">
                      <div className="text-right">
                        <span className="text-sm text-slate-500">Total Invoice Amount: </span>
                        <span className="text-lg font-semibold text-slate-900">₹{totalInvoiceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="skip_eway"
                        checked={formData.skip_eway_bill}
                        onCheckedChange={(checked) => setFormData({ ...formData, skip_eway_bill: checked })}
                      />
                      <Label htmlFor="skip_eway" className="cursor-pointer">Skip E-Way Bill</Label>
                    </div>
                  </div>

                  {!formData.skip_eway_bill && (
                    <div className="space-y-2">
                      <Label htmlFor="eway_bill_number">E-Way Bill Number</Label>
                      <Input
                        id="eway_bill_number"
                        value={formData.eway_bill_number}
                        onChange={(e) => setFormData({ ...formData, eway_bill_number: e.target.value })}
                        onBlur={(e) => setFormData({ ...formData, eway_bill_number: e.target.value.trim() })}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg mt-6">
                <CardHeader>
                  <CardTitle>Documents</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="invoice_doc">Invoice Document * (Max 20MB)</Label>
                    <Input
                      id="invoice_doc"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('invoice_document', e.target.files[0])}
                      /* No native `required`: the file input cannot be populated
                         programmatically, so "Attach as Invoice" (which sets
                         formData.invoice_document) would be blocked by constraint
                         validation before handleSubmit runs. handleSubmit enforces
                         the invoice instead, for both paths. */
                    />
                    <p className="text-xs text-slate-500">Accepted: PDF, JPEG, PNG, DOC, DOCX</p>
                    {aiAttachedAsInvoice && formData.invoice_document && (
                      <p className="text-xs" style={{ color: "#00A699" }}>
                        Using "{formData.invoice_document.name}" from Auto-fill above.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondary_doc">Secondary Document (Optional, Max 20MB)</Label>
                    <Input
                      id="secondary_doc"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange('secondary_document', e.target.files[0])}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end gap-4 mt-6">
                <Button type="button" variant="outline" onClick={() => navigate('/admin/orders')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || isSellerLowBalance || isBillingNotSet}
                  style={{backgroundColor: (isSellerLowBalance || isBillingNotSet) ? '#9CA3AF' : '#00A699'}}
                  className={(isSellerLowBalance || isBillingNotSet) ? 'cursor-not-allowed' : ''}
                >
                  {submitting ? "Creating..." : "Create Order"}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>

      {/* Owner's Risk acknowledgement popup */}
      <Dialog open={showOwnerRiskDialog} onOpenChange={setShowOwnerRiskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              Shipping with Owners Risk
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed text-slate-600">
              {"On selecting this option, the shipment will be shipped on owner's risk, with no insurance applicable from Carrier's end."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-2">
            <Button type="button" onClick={() => setShowOwnerRiskDialog(false)}>I Understand</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Drop Location Dialog */}
      <Dialog open={showAddDropLocationDialog} onOpenChange={setShowAddDropLocationDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Drop Location</DialogTitle>
            <DialogDescription>Add a new drop location for this order</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleAddTempDropLocation(); }} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="temp_company_name">Company Name *</Label>
                <Input
                  id="temp_company_name"
                  value={tempDropLocation.company_name}
                  onChange={(e) => setTempDropLocation({ ...tempDropLocation, company_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="temp_contact_person">Contact Person *</Label>
                <Input
                  id="temp_contact_person"
                  value={tempDropLocation.contact_person_name}
                  onChange={(e) => setTempDropLocation({ ...tempDropLocation, contact_person_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="temp_contact">Contact Number *</Label>
                <Input
                  id="temp_contact"
                  value={tempDropLocation.drop_contact}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setTempDropLocation({ ...tempDropLocation, drop_contact: value });
                  }}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  placeholder="10 digit mobile number"
                  required
                />
                {tempDropLocation.drop_contact && tempDropLocation.drop_contact.length !== 10 && (
                  <p className="text-xs text-red-500">Please enter a valid 10-digit mobile number</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="temp_email">Email</Label>
                <Input
                  id="temp_email"
                  type="email"
                  value={tempDropLocation.email}
                  onChange={(e) => setTempDropLocation({ ...tempDropLocation, email: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="temp_address">Address Line *</Label>
              <Input
                id="temp_address"
                value={tempDropLocation.address_line}
                onChange={(e) => setTempDropLocation({ ...tempDropLocation, address_line: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="temp_country">Country *</Label>
                <Input
                  id="temp_country"
                  value={tempDropLocation.country}
                  onChange={(e) => setTempDropLocation({ ...tempDropLocation, country: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="temp_pincode">Pincode *</Label>
                <div className="relative">
                  <Input
                    id="temp_pincode"
                    value={tempDropLocation.pincode}
                    maxLength={6}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setTempDropLocation({ ...tempDropLocation, pincode: value });
                      if (value.length === 6) lookupPincode(value);
                    }}
                    required
                  />
                  {pincodeLoading && <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin" />}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="temp_city">City *</Label>
                <Input
                  id="temp_city"
                  value={tempDropLocation.city}
                  onChange={(e) => setTempDropLocation({ ...tempDropLocation, city: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="temp_state">State *</Label>
                <Input
                  id="temp_state"
                  value={tempDropLocation.state}
                  onChange={(e) => setTempDropLocation({ ...tempDropLocation, state: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 p-4 bg-amber-50 rounded-md">
              <Checkbox
                id="save_for_future"
                checked={saveDropLocationForFuture}
                onCheckedChange={setSaveDropLocationForFuture}
              />
              <Label htmlFor="save_for_future" className="text-sm font-medium cursor-pointer">
                Save this location for future orders
              </Label>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowAddDropLocationDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" style={{backgroundColor: '#00A699'}}>
                Add Location
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CreateOrder;
